// Quarantine and treatment courses: the pure rules, shared by the server
// (validation) and /app (day counts, warnings). No I/O here.
//
// TankSensei records what the keeper does; it never proposes a medication
// or a dose. The product, the amount and the target come from the
// product's label, typed by the keeper, because a wrong number here harms
// livestock and the label is the only source that knows its own product.

export const COURSE_KINDS = ['quarantine', 'treatment'];
export const COURSE_LOCATIONS = ['separate', 'display'];
export const ENTRY_KINDS = ['observation', 'dose', 'test', 'water_change'];
export const COURSE_END_STATUSES = ['completed', 'stopped'];

// What a test entry measured. Each one charts on its own line: copper and
// ammonia are both read in ppm, and one line through both read as a copper
// crash. 'other' is never charted, since it can hold any test.
export const TEST_KINDS = ['copper', 'ammonia', 'nitrite', 'nitrate', 'salinity', 'ph', 'other'];
export const TEST_LABEL = {
  copper: 'Copper',
  ammonia: 'Ammonia',
  nitrite: 'Nitrite',
  nitrate: 'Nitrate',
  salinity: 'Salinity',
  ph: 'pH',
  other: 'Other',
};

export const MAX_TITLE = 80;
export const MAX_TEXT = 500;
export const MAX_PLANNED_DAYS = 365;

// Copper-based treatments kill corals and invertebrates, which is why they
// belong in a separate tank. The names are the common copper products and
// the word itself; a miss only means no warning, never a wrong one. API's
// SeaCure is a chelated copper sold without the word on its name.
const COPPER_RE = /\b(copper|cupramine|cuprion|coppersafe|sea\s?cure)\b/i;

export function isCopperProduct(product) {
  return COPPER_RE.test(String(product || ''));
}

// The latest calendar day a keeper anywhere can be on. Clients send their
// own local date; east of UTC that is already tomorrow in UTC terms.
function latestLocalDay(now) {
  return new Date(new Date(now).getTime() + 14 * 3600000).toISOString().slice(0, 10);
}

function dayStart(d) {
  const t = new Date(d);
  return Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
}

// Day 1 is the start date. `today` is the keeper's own date (YYYY-MM-DD);
// a course with no planned length has no total.
export function courseProgress(course, today = new Date().toISOString().slice(0, 10)) {
  const started = course?.started_on ? dayStart(String(course.started_on).slice(0, 10)) : null;
  if (started == null || Number.isNaN(started)) return null;
  const end =
    course.status && course.status !== 'active' && course.ended_on
      ? dayStart(String(course.ended_on).slice(0, 10))
      : dayStart(today);
  const day = Math.max(1, Math.floor((end - started) / 86400000) + 1);
  const planned = Number(course.planned_days) || null;
  return {
    day,
    planned,
    remaining: planned ? Math.max(0, planned - day) : null,
    due: planned ? day >= planned : false,
  };
}

// What the keeper should hear before or during a course. Plain facts,
// never a dose.
export function courseWarnings(course, { reefLife = false } = {}) {
  const out = [];
  if (isCopperProduct(course?.product)) {
    if (course.location === 'display' && reefLife) {
      out.push(
        'Copper kills corals and invertebrates, and this tank has them: treat the fish in a separate tank without corals or invertebrates instead.'
      );
    } else if (course.location === 'display') {
      out.push(
        'Copper kills corals and invertebrates. Rock and sand can take up copper and release it later, so keep them out of a tank while copper is in it.'
      );
    }
    out.push(
      'Test copper on the schedule the label gives, with a copper test the label names or allows, and log each result here.'
    );
  }
  return out;
}

function text(v, max) {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s.slice(0, max) : null;
}

function isoDate(v) {
  if (v == null || v === '') return null;
  const s = String(v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined;
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== s ? undefined : s;
}

export function validateCourse(body, { now = new Date() } = {}) {
  const kind = COURSE_KINDS.includes(body?.kind) ? body.kind : null;
  if (!kind) return { error: 'Choose quarantine or treatment.' };
  const title = text(body.title, MAX_TITLE);
  if (!title) return { error: 'Give it a name, for example the fish or the problem.' };
  const started =
    body.started_on == null || body.started_on === '' ? null : isoDate(body.started_on);
  if (started === undefined) return { error: 'The start date is not a date.' };
  const today = new Date(now).toISOString().slice(0, 10);
  if (started && started > latestLocalDay(now)) {
    return { error: 'The start date is in the future.' };
  }
  let planned = null;
  if (body.planned_days != null && body.planned_days !== '') {
    planned = Number(body.planned_days);
    if (!Number.isInteger(planned) || planned < 1 || planned > MAX_PLANNED_DAYS) {
      return { error: `Planned length is a whole number of days, 1 to ${MAX_PLANNED_DAYS}.` };
    }
  }
  const location = COURSE_LOCATIONS.includes(body.location) ? body.location : 'separate';
  const residents = Array.isArray(body.livestock_ids)
    ? [...new Set(body.livestock_ids.map(String).filter(Boolean))].slice(0, 50)
    : [];
  return {
    value: {
      kind,
      title,
      reason: text(body.reason, MAX_TEXT),
      product: text(body.product, 120),
      label_target: text(body.label_target, 200),
      location,
      started_on: started || today,
      planned_days: planned,
      remind_daily: body.remind_daily !== false,
      livestock_ids: residents,
    },
  };
}

// `notBefore` is the course's start date: an entry dated earlier belongs to
// no course. A day of slack covers a keeper east of UTC logging in the
// first hours of the start date.
export function validateEntry(body, { now = new Date(), notBefore = null } = {}) {
  const kind = ENTRY_KINDS.includes(body?.kind) ? body.kind : null;
  if (!kind) return { error: 'Choose what you are logging.' };
  const note = text(body.note, MAX_TEXT);
  let amount = null;
  if (body.amount != null && body.amount !== '') {
    amount = Number(body.amount);
    if (!Number.isFinite(amount) || amount < 0 || amount > 1e6) {
      return { error: 'The amount is not a number.' };
    }
  }
  const unit = text(body.unit, 20);
  let test = null;
  if (kind === 'test' && body.test != null && body.test !== '') {
    if (!TEST_KINDS.includes(body.test)) return { error: 'Choose what you tested.' };
    test = body.test;
  }
  if ((kind === 'dose' || kind === 'test') && amount == null) {
    return {
      error: kind === 'dose' ? 'Enter how much you dosed.' : 'Enter the test result.',
    };
  }
  // A photo on its own is an observation too; it follows in its own request.
  if (kind === 'observation' && !note && !body.with_photos) {
    return { error: 'Write what you saw, or add a photo.' };
  }
  let ts = new Date(now);
  if (body.ts) {
    const d = new Date(body.ts);
    if (Number.isNaN(d.getTime())) return { error: 'The time is not a time.' };
    if (d.getTime() > ts.getTime() + 5 * 60000) return { error: 'The time is in the future.' };
    const floor = notBefore ? new Date(notBefore).getTime() - 86400000 : null;
    if (floor != null && Number.isFinite(floor) && d.getTime() < floor) {
      return { error: 'That is before this started.' };
    }
    ts = d;
  }
  return { value: { kind, amount, unit, note, ts, test } };
}

export function validateFinish(body, { startedOn, now = new Date() } = {}) {
  const status = COURSE_END_STATUSES.includes(body?.status) ? body.status : null;
  if (!status) return { error: 'Choose finished or stopped.' };
  const ended = body.ended_on == null || body.ended_on === '' ? null : isoDate(body.ended_on);
  if (ended === undefined) return { error: 'The end date is not a date.' };
  const endedOn = ended || new Date(now).toISOString().slice(0, 10);
  if (endedOn > latestLocalDay(now)) return { error: 'The end date is in the future.' };
  const start = startedOn ? new Date(startedOn).toISOString().slice(0, 10) : null;
  if (start && endedOn < start) return { error: 'The end date is before the start.' };
  return { value: { status, ended_on: endedOn, outcome: text(body.outcome, MAX_TEXT) } };
}

// What a test entry measured: its own field, or for an entry logged
// before that field existed, the one test its note names ("copper, Hanna
// checker"). On a copper course an untagged entry whose note names no test
// is copper: the old log pre-filled ppm for a copper product, charted every
// test, and most of those entries have no note, so they would drop off the
// chart. Anywhere else a note naming none, or two, leaves it unknown and
// off the chart.
const NOTE_TEST_RE = /\b(copper|ammonia|nitrite|nitrate|salinity|ph)\b/gi;
export function entryTest(e, { copperCourse = false } = {}) {
  if (e?.test && TEST_KINDS.includes(e.test)) return e.test;
  const found = new Set(
    [...String(e?.note || '').matchAll(NOTE_TEST_RE)].map((m) => m[1].toLowerCase())
  );
  if (found.size === 1) return [...found][0];
  if (found.size === 0 && copperCourse) return 'copper';
  return null;
}

// The logged test results worth charting, one series per test (copper,
// ammonia...), each in the unit most of its results used, oldest first.
// Two points make a line; fewer is no chart. A result in another unit, an
// 'other' test or one we cannot name stays in the log only.
export function testSeriesByTest(entries, { copperCourse = false } = {}) {
  const groups = new Map();
  for (const e of entries || []) {
    if (e.kind !== 'test' || e.amount == null || !Number.isFinite(Number(e.amount))) continue;
    const test = entryTest(e, { copperCourse });
    if (!test || test === 'other') continue;
    if (!groups.has(test)) groups.set(test, []);
    groups.get(test).push(e);
  }
  const out = [];
  for (const test of TEST_KINDS) {
    const tests = groups.get(test);
    if (!tests || tests.length < 2) continue;
    const byUnit = new Map();
    for (const e of tests) {
      const u = String(e.unit || '').trim();
      byUnit.set(u, (byUnit.get(u) || 0) + 1);
    }
    let unit = null;
    let best = 0;
    for (const [u, n] of byUnit) {
      if (n > best) {
        unit = u;
        best = n;
      }
    }
    const points = tests
      .filter((e) => String(e.unit || '').trim() === unit)
      .map((e) => ({ ts: new Date(e.ts).getTime(), value: Number(e.amount) }))
      .filter((p) => Number.isFinite(p.ts))
      .sort((a, b) => a.ts - b.ts);
    if (points.length >= 2) out.push({ test, label: TEST_LABEL[test], unit, points });
  }
  return out;
}

// A course test the keeper can also save as one of the tank's own readings
// (opt-in, a course in this tank only): the tests that have a parameter
// card, read in that card's unit. The reading then goes through the
// readings route like any other, whose rules decide everything after.
const TEST_READING_UNITS = {
  ammonia: ['ppm', 'mg/l'],
  nitrite: ['ppm', 'mg/l'],
  nitrate: ['ppm', 'mg/l'],
  ph: ['', 'ph'],
  salinity: ['', 'ppt', 'psu', 'sg', 's.g.'],
};

export function courseTestAsReading(entry, tankParams) {
  const param = entry?.test;
  const units = TEST_READING_UNITS[param];
  if (!units) return null;
  const card = tankParams?.[param];
  if (!card || card.hidden) return null;
  const amount = Number(entry.amount);
  if (entry.amount == null || entry.amount === '' || !Number.isFinite(amount)) return null;
  const unit = String(entry.unit || '')
    .trim()
    .toLowerCase();
  const cardUnit = String(card.unit || '')
    .trim()
    .toLowerCase();
  if (!units.includes(unit) && !(unit && unit === cardUnit)) return null;
  return {
    param,
    value: amount,
    unit: param === 'ph' || param === 'salinity' ? null : card.unit || 'ppm',
  };
}

// One-tap words for a daily look at a fish in quarantine: what a keeper
// checks, not what it means. Tapping one adds it to the note; the keeper
// can say more. No diagnosis is offered from them.
export const OBSERVATION_CHIPS = [
  'Eating well',
  'Not eating',
  'White spots',
  'Scratching or flashing',
  'Fast breathing',
  'Cloudy eyes',
  'Hiding',
  'No signs today',
];
